import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import { useUploadListContext } from "src/contexts/UploadListContext";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridSortChangeEvent,
  GridPageChangeEvent,
} from "@progress/kendo-react-grid";
import {
  SortDescriptor,
  orderBy,
} from "@progress/kendo-data-query";
import styles from "./CallingTimes.module.css";

// --- Types ---

interface SelectOption {
  value: string;
  text: string;
}

interface CallingWindowRow {
  callingWindowId: number;
  state: string;
  weekdayWindow: string;
  satWindow: string;
  sunWindow: string;
}

interface CallingWindowFormData {
  country: string;
  stateUS: string;
  stateCA: string;
  weekdayStartTime: string;
  weekdayEndTime: string;
  saturdayStartTime: string;
  saturdayEndTime: string;
  sundayStartTime: string;
  sundayEndTime: string;
}

const PAGE_SIZE_KEY = "callingTimes_pageSize";
const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZES = [10, 25, 50, 100];

const DEFAULT_FORM: CallingWindowFormData = {
  country: "US",
  stateUS: "",
  stateCA: "",
  weekdayStartTime: "09:00",
  weekdayEndTime: "21:30",
  saturdayStartTime: "10:00",
  saturdayEndTime: "18:00",
  sundayStartTime: "10:00",
  sundayEndTime: "18:00",
};

const CallingTimes: React.FC = () => {
  const navigate = useNavigate();
  const { projectId, campaignId } = useUploadListContext();

  // Form options (regions)
  const [regionsUS, setRegionsUS] = useState<SelectOption[]>([]);
  const [regionsCA, setRegionsCA] = useState<SelectOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Grid state
  const [windows, setWindows] = useState<CallingWindowRow[]>([]);
  const [sort, setSort] = useState<SortDescriptor[]>([
    { field: "state", dir: "asc" },
  ]);
  const [page, setPage] = useState({
    skip: 0,
    take: Number(localStorage.getItem(PAGE_SIZE_KEY)) || DEFAULT_PAGE_SIZE,
  });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<CallingWindowFormData>({ ...DEFAULT_FORM });
  // When editing, we track the original state to load existing times
  const [editingState, setEditingState] = useState<string | null>(null);

  // Load form options on mount
  useEffect(() => {
    if (projectId && campaignId) {
      loadFormOptions();
      loadWindows();
    }
  }, [projectId, campaignId]);

  const loadFormOptions = async () => {
    try {
      const data = await callApi(
        `main/customcallingwindow/getformoptions?campaignId=${campaignId}`,
        HttpMethod.GET,
      );
      setRegionsUS(data.regionsUS);
      setRegionsCA(data.regionsCA);
    } catch (err: any) {
      toast.error(err.message || "Failed to load form options");
    } finally {
      setIsLoading(false);
    }
  };

  const loadWindows = async () => {
    if (!campaignId) return;
    try {
      const data = await callApi(
        `main/customcallingwindow/getcallingwindows?campaignId=${campaignId}`,
        HttpMethod.GET,
      );
      setWindows(data.records ?? []);
    } catch (err: any) {
      toast.error(err.message || "Failed to load calling windows");
    }
  };

  // Open modal for add
  const openAddModal = () => {
    setForm({ ...DEFAULT_FORM });
    setEditingState(null);
    setShowModal(true);
  };

  // Open modal for edit — load existing times for the state
  const openEditModal = async (state: string) => {
    try {
      const data = await callApi(
        `main/customcallingwindow/getcallingwindow?campaignId=${campaignId}&state=${encodeURIComponent(state)}`,
        HttpMethod.GET,
      );
      // Determine country from state abbreviation
      const isUS = regionsUS.some((r) => r.value === state);
      setForm({
        country: isUS ? "US" : "CA",
        stateUS: isUS ? state : "",
        stateCA: isUS ? "" : state,
        weekdayStartTime: data.weekdayStartTime,
        weekdayEndTime: data.weekdayEndTime,
        saturdayStartTime: data.saturdayStartTime,
        saturdayEndTime: data.saturdayEndTime,
        sundayStartTime: data.sundayStartTime,
        sundayEndTime: data.sundayEndTime,
      });
      setEditingState(state);
      setShowModal(true);
    } catch (err: any) {
      toast.error(err.message || "Failed to load calling window");
    }
  };

  const handleDelete = async (row: CallingWindowRow) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the calling window for ${row.state}?`,
      )
    )
      return;
    try {
      await callApi(
        `main/customcallingwindow/deletecallingwindow?id=${row.callingWindowId}&campaignId=${campaignId}`,
        HttpMethod.DELETE,
      );
      toast.success("Calling window deleted");
      loadWindows();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete calling window");
    }
  };

  const ActionsCell = (props: GridCellProps) => {
    const row = props.dataItem as CallingWindowRow;
    return (
      <td>
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => openEditModal(row.state)}
          >
            Edit
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDelete(row)}
          >
            Delete
          </button>
        </div>
      </td>
    );
  };

  // Save calling window
  const handleSave = async () => {
    const state = form.country === "US" ? form.stateUS : form.stateCA;
    if (!state) {
      toast.error("State/Province is required");
      return;
    }

    // Validate times
    if (form.weekdayStartTime >= form.weekdayEndTime) {
      toast.error("Weekday start time must be before end time");
      return;
    }
    if (form.saturdayStartTime >= form.saturdayEndTime) {
      toast.error("Saturday start time must be before end time");
      return;
    }
    if (form.sundayStartTime >= form.sundayEndTime) {
      toast.error("Sunday start time must be before end time");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await callApi(
        `main/customcallingwindow/savecallingwindow?campaignId=${campaignId}`,
        HttpMethod.POST,
        {
          country: form.country,
          stateUS: form.stateUS,
          stateCA: form.stateCA,
          weekdayStartTime: form.weekdayStartTime,
          weekdayEndTime: form.weekdayEndTime,
          saturdayStartTime: form.saturdayStartTime,
          saturdayEndTime: form.saturdayEndTime,
          sundayStartTime: form.sundayStartTime,
          sundayEndTime: form.sundayEndTime,
        },
      );
      if (result.result) {
        toast.success("Calling window saved successfully");
        setShowModal(false);
        loadWindows();
      } else {
        toast.error(result.message || "Failed to save calling window");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save calling window");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Form updater
  const updateForm = (field: keyof CallingWindowFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Grid data processing
  const processedData = useMemo(() => {
    let data = [...windows];
    if (sort.length > 0) data = orderBy(data, sort);
    return data;
  }, [windows, sort]);

  const pagedData = useMemo(() => {
    return processedData.slice(page.skip, page.skip + page.take);
  }, [processedData, page]);

  const handleSortChange = (e: GridSortChangeEvent) => setSort(e.sort);
  const handlePageChange = (e: GridPageChangeEvent) => {
    setPage(e.page);
    localStorage.setItem(PAGE_SIZE_KEY, String(e.page.take));
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className="d-flex justify-content-center p-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h5 className="mb-0">Configure Non Default Calling Windows</h5>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => navigate(`/main/project-settings/${campaignId}`)}
        >
          Back to Project Settings
        </button>
      </div>

      <p className="text-muted">
        By default, the scrubber will only allow calls to be placed between the
        jurisdiction's statutory calling windows for telephone solicitations.
        You can set a custom calling window by jurisdiction below.
      </p>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <button className="btn btn-outline-secondary btn-sm" onClick={openAddModal}>
          Add Non-Default Calling Window
        </button>
      </div>

      {/* Grid */}
      <Grid
        data={pagedData}
        total={processedData.length}
        dataItemKey="callingWindowId"
        sortable={{ mode: "multiple", allowUnsort: true }}
        sort={sort}
        onSortChange={handleSortChange}
        pageable={{ pageSizes: PAGE_SIZES }}
        skip={page.skip}
        take={page.take}
        onPageChange={handlePageChange}
        style={{ height: "calc(100vh - 300px)" }}
      >
        <Column field="state" title="Region" width={200} />
        <Column field="weekdayWindow" title="Weekday" width={250} />
        <Column field="satWindow" title="Saturday" width={250} />
        <Column field="sunWindow" title="Sunday" width={250} />
        <Column
          title="Actions"
          width={160}
          filterable={false}
          sortable={false}
          cells={{ data: ActionsCell }}
        />
      </Grid>

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          className="modal d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingState
                    ? "Edit Custom Calling Window"
                    : "Add Custom Calling Window"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                />
              </div>
              <div className="modal-body">
                {/* Country & State */}
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Country</label>
                    <select
                      className="form-select"
                      value={form.country}
                      onChange={(e) => updateForm("country", e.target.value)}
                      disabled={editingState !== null}
                    >
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                    </select>
                  </div>
                  <div className={styles.formGroup}>
                    <label>State / Province</label>
                    {form.country === "US" ? (
                      <select
                        className="form-select"
                        value={form.stateUS}
                        onChange={(e) => updateForm("stateUS", e.target.value)}
                        disabled={editingState !== null}
                      >
                        <option value="">-- Select --</option>
                        {regionsUS.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.text}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <select
                        className="form-select"
                        value={form.stateCA}
                        onChange={(e) => updateForm("stateCA", e.target.value)}
                        disabled={editingState !== null}
                      >
                        <option value="">-- Select --</option>
                        {regionsCA.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.text}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                {/* Time inputs */}
                <div className={styles.timeSection}>
                  <label className="mb-2">Calling Hours</label>

                  <div className={styles.timeRow}>
                    <span className={styles.dayLabel}>Weekday</span>
                    <input
                      type="time"
                      className="form-control"
                      value={form.weekdayStartTime}
                      onChange={(e) =>
                        updateForm("weekdayStartTime", e.target.value)
                      }
                      style={{ maxWidth: 140 }}
                    />
                    <span>to</span>
                    <input
                      type="time"
                      className="form-control"
                      value={form.weekdayEndTime}
                      onChange={(e) =>
                        updateForm("weekdayEndTime", e.target.value)
                      }
                      style={{ maxWidth: 140 }}
                    />
                  </div>

                  <div className={styles.timeRow}>
                    <span className={styles.dayLabel}>Saturday</span>
                    <input
                      type="time"
                      className="form-control"
                      value={form.saturdayStartTime}
                      onChange={(e) =>
                        updateForm("saturdayStartTime", e.target.value)
                      }
                      style={{ maxWidth: 140 }}
                    />
                    <span>to</span>
                    <input
                      type="time"
                      className="form-control"
                      value={form.saturdayEndTime}
                      onChange={(e) =>
                        updateForm("saturdayEndTime", e.target.value)
                      }
                      style={{ maxWidth: 140 }}
                    />
                  </div>

                  <div className={styles.timeRow}>
                    <span className={styles.dayLabel}>Sunday</span>
                    <input
                      type="time"
                      className="form-control"
                      value={form.sundayStartTime}
                      onChange={(e) =>
                        updateForm("sundayStartTime", e.target.value)
                      }
                      style={{ maxWidth: 140 }}
                    />
                    <span>to</span>
                    <input
                      type="time"
                      className="form-control"
                      value={form.sundayEndTime}
                      onChange={(e) =>
                        updateForm("sundayEndTime", e.target.value)
                      }
                      style={{ maxWidth: 140 }}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallingTimes;
